import PlusIcon from '@components/icons/PlusIcon';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { CircularProgress } from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { companyProfileService } from '@services/requests/companyProfileService';
import { DocumentFile } from '@services/requests/companyProfileService/interface';
import { DocumentsFields } from '@services/requests/companyProfileService/types/mainData';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseFormReturn } from 'react-hook-form';

import {
  CompanyProfileExtendedFormFields,
  CompanyProfileFormFields,
} from '../../CompanyProfileModulesWrapper/types';

interface RequiredDocumentItemProps {
  fields: DocumentsFields;
  document: keyof DocumentsFields;
  form: UseFormReturn<
    CompanyProfileFormFields & CompanyProfileExtendedFormFields,
    any,
    CompanyProfileFormFields & CompanyProfileExtendedFormFields
  >;
  isInspection: boolean;
}

function RequiredDocumentItem({ fields, document, form, isInspection }: RequiredDocumentItemProps) {
  const [fileData, setFileData] = useState<DocumentFile | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const {
    setValue,
    formState: { errors },
  } = form;

  useEffect(() => {
    return () => setValue(document, undefined);
  }, [document]);

  const { enqueueSnackbar } = useSnackbar();
  const { getRootProps, getInputProps } = useDropzone({
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    onDrop: async (files: File[]) => {
      if (files.length < 1) {
        return;
      }

      setIsUploading(true);
      const file = files[0];
      const result = await companyProfileService().UploadRequiredDocument(bitrixUser.id, { file });
      setIsUploading(false);

      if ('errors' in result) {
        enqueueSnackbar(result.errors.join(' '), { variant: 'error' });
      }

      if (!result.success) {
        enqueueSnackbar('Не удалось загрузить файл. Попробуйте снова', { variant: 'error' });
        return;
      }

      setFileData(result.data);
      setValue(document, result.data);
    },
  });

  const resetFileHandler = () => {
    setFileData(null);
    setValue(document, undefined);
  };

  if (fileData) {
    return (
      <TextField
        fullWidth
        className="document-file"
        size={'medium'}
        disabled
        type={'text'}
        label={fields[document].title}
        variant="outlined"
        value={fileData.title}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={'reset document file'}
                onClick={resetFileHandler}
                disabled={isInspection}
                edge="end"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return (
    <div {...getRootProps({ className: clsx(`upload-file-btn`, { error: !!errors[document] }) })}>
      <span className="upload-file-btn__label">{fields[document].title}</span>
      <input {...getInputProps()} />
      {!isUploading && <PlusIcon />}
      {isUploading && <CircularProgress color="inherit" size={22} />}
    </div>
  );
}

export { RequiredDocumentItem };
